import { FC, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { Passwordbox, Textbox } from "capitalrx-components-2.0/lib/forms";
import { Grid, Shelf } from "capitalrx-components-2.0/lib/layout";
import { Subheading } from "capitalrx-components-2.0/lib/typography";
import { email as validateEmail } from "capitalrx-utilities/lib/form-validation";
import { ThemedCheckbox, ThemedSubmitButton } from "@/App/components";

export interface ConfirmRegistrationFormProps {
  readonly stepNumber: string;
  readonly forceEmailReadonly: boolean;
}

export const ConfirmRegistrationForm: FC<ConfirmRegistrationFormProps> = ({ stepNumber, forceEmailReadonly }) => {
  const form = useFormContext();
  const location = useLocation();
  const { t: translate } = useTranslation();
  const isSSOPreregister = useMemo(() => {
    return location.pathname === "/sso/register";
  }, [location]);

  const isValidEmail = validateEmail(form.getValues("email")) === undefined;

  if (!isValidEmail) {
    form.setValue("optIntoEmails", false);
  }

  const validatePasswordMatch = () => {
    const password = form.watch("password");
    const confirmPassword = form.watch("confirmPassword");
    if (password != confirmPassword) {
      return translate("REGISTER.confirmRegistrationForm.passwordsDoNotMatch");
    } else {
      return undefined;
    }
  };

  return (
    <Grid>
      <Grid.Span columns={12}>
        <Subheading textAlign="center">{stepNumber}. {isValidEmail ? translate("REGISTER.confirmRegistrationForm.enterEmailAndPassword") : translate("REGISTER.confirmRegistrationForm.confirmUserName")}</Subheading>
      </Grid.Span>

      <Grid.Span columns={12}>
        <Textbox.Field testId="email" readOnly={forceEmailReadonly} placeholder="Email" name="email" required label={isValidEmail ? translate("REGISTER.confirmRegistrationForm.email") : translate("REGISTER.confirmRegistrationForm.userName")}/>
      </Grid.Span>
      {!isSSOPreregister && (
        <Grid.Span columns={12}>
          <Passwordbox.Field testId="password" placeholder={translate("REGISTER.confirmRegistrationForm.password")} name="password" required label={translate("REGISTER.confirmRegistrationForm.password")} />
        </Grid.Span>
      )}
      {!isSSOPreregister && (
        <Grid.Span columns={12}>
          <Passwordbox.Field
            testId="confirm-password"
            validator={validatePasswordMatch}
            placeholder={translate("REGISTER.confirmRegistrationForm.confirmPassword")}
            name="confirmPassword"
            required
            label={translate("REGISTER.confirmRegistrationForm.confirmPassword")}
          />
        </Grid.Span>
      )}
      {isValidEmail && <Grid.Span columns={12}>
        <Shelf spacing="none">
          <Shelf.Spacer />
          <ThemedCheckbox.Field testId="opt-into-emails" name="optIntoEmails" label={translate("REGISTER.confirmRegistrationForm.receiveEmails")} />
          <Shelf.Spacer />
        </Shelf>
      </Grid.Span>}

      <ThemedSubmitButton testId="create-account" appearance="primary" icon="arrow-right" iconPosition="right">
        {translate("REGISTER.confirmRegistrationForm.createAccount")}
      </ThemedSubmitButton>
    </Grid>
  );
};
